/**
 * Placement automatique des joueurs en préparation de table.
 *
 * Priorités :
 * - Conserver les joueurs déjà placés
 * - Legacy entrée libre : sièges 0..k−1 (humains groupés, compatible Java)
 * - Sinon : bloc contigu à partir du Sud, préférences de rôle si possible
 * - Défi 2 tables : remplissage Sud T1, Sud T2, Est T1, Est T2, …
 */

import type {
  DraftPlayer,
  DraftTableConfig,
  SeatPosition,
  CompetRolePreference,
} from '../types/draft';
import { getActiveSeats } from '../types/draft';

export type SeatAssignment = {
  playerPseudo: string;
  seat: SeatPosition;
  tableIndex: number;
};

function norm(pseudo: string): string {
  return pseudo.toLowerCase();
}

export function isPlayerSeated(player: DraftPlayer, dualTable: boolean): boolean {
  if (!player.seat) return false;
  if (dualTable && player.tableIndex === undefined) return false;
  return true;
}

function sortByCreatorThenJoin(
  list: DraftPlayer[],
  createdBy: string,
): DraftPlayer[] {
  return [...list].sort((a, b) => {
    if (norm(a.pseudo) === norm(createdBy)) return -1;
    if (norm(b.pseudo) === norm(createdBy)) return 1;
    return a.joinedAt - b.joinedAt;
  });
}

function preferredSeatsForRole(
  pref: CompetRolePreference,
  compet: boolean,
  seats: SeatPosition[],
): SeatPosition[] {
  if (!compet || seats.length !== 4) return [...seats];
  if (pref === 'attacker') return ['attaquant'];
  if (pref === 'defender') return ['def_E', 'def_N', 'def_O'];
  return [...seats];
}

function pickPlayerForSeat(
  pool: DraftPlayer[],
  seat: SeatPosition,
  compet: boolean,
  rolePrefs: Map<string, CompetRolePreference>,
): DraftPlayer | null {
  if (pool.length === 0) return null;

  const isAttackerSeat = compet && seat === 'attaquant';
  const isDefenderSeat = compet && seat !== 'attaquant';

  const score = (p: DraftPlayer): number => {
    const pref = rolePrefs.get(norm(p.pseudo)) ?? 'none';
    if (isAttackerSeat) {
      if (pref === 'attacker') return 3;
      if (pref === 'none') return 2;
      return 0;
    }
    if (isDefenderSeat) {
      if (pref === 'defender') return 3;
      if (pref === 'none') return 2;
      return 0;
    }
    return 1;
  };

  let bestIdx = 0;
  let bestScore = -1;
  for (let i = 0; i < pool.length; i++) {
    const s = score(pool[i]);
    if (s > bestScore) {
      bestScore = s;
      bestIdx = i;
    }
  }
  if (bestScore <= 0 && pool.length > 1) {
    const neutralIdx = pool.findIndex(
      (p) => (rolePrefs.get(norm(p.pseudo)) ?? 'none') === 'none',
    );
    if (neutralIdx >= 0) bestIdx = neutralIdx;
  }

  return pool.splice(bestIdx, 1)[0];
}

function buildSingleTablePlacement(
  joined: DraftPlayer[],
  seats: SeatPosition[],
  createdBy: string,
  alreadyPlaced: SeatAssignment[],
  rolePrefs: Map<string, CompetRolePreference>,
  compet: boolean,
  legacyLibreEntry: boolean,
): SeatAssignment[] {
  const tableIndex = 0;
  const occupied = new Set(alreadyPlaced.map((s) => `${s.tableIndex}:${s.seat}`));
  const placedPseudos = new Set(alreadyPlaced.map((s) => norm(s.playerPseudo)));
  const unplaced = joined.filter((p) => !placedPseudos.has(norm(p.pseudo)));
  const result = [...alreadyPlaced];

  if (legacyLibreEntry) {
    for (const player of sortByCreatorThenJoin(unplaced, createdBy)) {
      for (const seat of seats) {
        const key = `${tableIndex}:${seat}`;
        if (!occupied.has(key)) {
          result.push({ playerPseudo: player.pseudo, seat, tableIndex });
          occupied.add(key);
          break;
        }
      }
    }
    return result;
  }

  const humanCount = joined.length;
  const seatIndices = Array.from(
    { length: Math.min(humanCount, seats.length) },
    (_, i) => i,
  );

  const pool = sortByCreatorThenJoin(unplaced, createdBy);
  for (const idx of seatIndices) {
    const seat = seats[idx];
    const key = `${tableIndex}:${seat}`;
    if (occupied.has(key)) continue;
    if (pool.length === 0) break;

    let assigned: DraftPlayer | null = null;

    for (const player of [...pool]) {
      const pref = rolePrefs.get(norm(player.pseudo)) ?? 'none';
      const preferred = preferredSeatsForRole(pref, compet, seats);
      if (preferred.includes(seat)) {
        assigned = player;
        pool.splice(pool.indexOf(player), 1);
        break;
      }
    }

    if (!assigned) {
      assigned = pickPlayerForSeat(pool, seat, compet, rolePrefs);
      if (assigned) pool.splice(pool.indexOf(assigned), 1);
    }

    if (assigned) {
      result.push({ playerPseudo: assigned.pseudo, seat, tableIndex });
      occupied.add(key);
    }
  }

  return result;
}

function buildDualTablePlacement(
  joined: DraftPlayer[],
  seats: SeatPosition[],
  createdBy: string,
  alreadyPlaced: SeatAssignment[],
  rolePrefs: Map<string, CompetRolePreference>,
  compet: boolean,
): SeatAssignment[] {
  const fillOrder: Array<{ tableIndex: number; seat: SeatPosition }> = [];
  for (const seat of seats) {
    for (const tableIndex of [0, 1]) {
      fillOrder.push({ tableIndex, seat });
    }
  }

  const result = [...alreadyPlaced];
  const occupied = new Set(result.map((s) => `${s.tableIndex}:${s.seat}`));
  const placedPseudos = new Set(result.map((s) => norm(s.playerPseudo)));
  const pool = sortByCreatorThenJoin(
    joined.filter((p) => !placedPseudos.has(norm(p.pseudo))),
    createdBy,
  );

  for (const { tableIndex, seat } of fillOrder) {
    const key = `${tableIndex}:${seat}`;
    if (occupied.has(key) || pool.length === 0) continue;

    const player = pickPlayerForSeat(pool, seat, compet, rolePrefs);
    if (!player) break;

    result.push({ playerPseudo: player.pseudo, seat, tableIndex });
    occupied.add(key);
  }

  return result;
}

export function seatsAssignmentsEqual(
  a: SeatAssignment[],
  b: SeatAssignment[],
): boolean {
  if (a.length !== b.length) return false;
  const key = (s: SeatAssignment) =>
    `${norm(s.playerPseudo)}:${s.tableIndex}:${s.seat}`;
  const sortedA = a.map(key).sort();
  const sortedB = b.map(key).sort();
  return sortedA.every((v, i) => v === sortedB[i]);
}

export interface AutoPlacementParams {
  players: DraftPlayer[];
  config: DraftTableConfig;
  compet: boolean;
  createdBy: string;
  rolePreferences: Map<string, CompetRolePreference>;
}

export function computeAutoPlacement(params: AutoPlacementParams): SeatAssignment[] {
  const { players, config, compet, createdBy, rolePreferences } = params;
  const dualTable = config.gameMode === 'defi_libre' && config.nbTables === 2;
  const nbJoueurs = config.gameMode === 'defi_libre' ? 4 : config.nombreJoueurs;
  const seats = getActiveSeats(nbJoueurs, compet);
  const joined = players.filter((p) => p.status === 'joined');

  const alreadyPlaced = joined
    .filter((p) => isPlayerSeated(p, dualTable))
    .map((p) => ({
      playerPseudo: p.pseudo,
      seat: p.seat!,
      tableIndex: p.tableIndex ?? 0,
    }));

  const legacyLibreEntry =
    config.tableType === 'legacy'
    && config.gameMode === 'libre'
    && config.modeEntree !== 2;

  if (dualTable) {
    return buildDualTablePlacement(
      joined,
      seats,
      createdBy,
      alreadyPlaced,
      rolePreferences,
      compet,
    );
  }

  return buildSingleTablePlacement(
    joined,
    seats,
    createdBy,
    alreadyPlaced,
    rolePreferences,
    compet,
    legacyLibreEntry,
  );
}
